<ng-container *ngIf="viewModel$ | async as vm">
  <app-dashboard-shell
    title="OTR - System Revenue"
    [lastReload]="vm.lastReload"
    [loading]="vm.loading"
    [showRefresh]="true"
    (refresh)="triggerReload()"
    class="otr-system-revenue-container"
  >
    <div class="p-col-12">
      <div class="dashboard-card">
        <div class="dashboard-card-header p-mb-3">
          <div>
            <div class="title-row">
              <h4 class="p-mb-0">
                OTR Revenue by Franchise<app-error-icon
                  *ngIf="vm.error"
                  [error]="vm.error"
                ></app-error-icon>
              </h4>
              <app-dashboard-info-v2>
                <div class="p-mb-2">
                  OTR revenue breakdown by franchise for jobs whose last
                  scheduled event ends in the selected period (regardless of
                  completion status).
                </div>
                <ul class="p-mb-2 p-pl-3">
                  <li><strong>Local</strong> — the franchise's internal revenue (matches the System Revenue dashboard).</li>
                  <li><strong>OTR</strong> — franchise-partner (FP) revenue on interstate jobs shared into this franchise.</li>
                  <li><strong>OTR Jobs</strong> — count of jobs shared into this franchise.</li>
                  <li><strong>Flat Labor</strong> — subcontractor labor portion on those shared-in jobs.</li>
                  <li><strong>Total</strong> — Local + OTR + Flat Labor.</li>
                  <li>
                    Where a crew member is marked <strong>Pay Labor to FP</strong>, their labor is
                    paid by the franchise partner rather than to the crew, so that amount moves out
                    of Flat Labor and into OTR. Total is unchanged. The drill-down's
                    <em>Labor Forwarded to FP</em> column shows how much moved on each job.
                  </li>
                  <!-- #6765: the single Interstate "OTR Margin" figure was replaced by a heading
                       plus three component rows, and two rows were added. Kept in sync with the
                       table deliberately — this panel is where a reader works out why a number
                       moved, so a stale entry here is worse than none. -->
                  <li><strong>OTR Direct</strong> — jobs OTR runs itself. Listed with the franchises
                      because the payout is real, but there is no franchise partner, so Local is blank.
                      The other columns follow the same shape as a franchise row even though nothing is
                      shared: <strong>OTR Jobs</strong> counts the OTR-Operated jobs,
                      <strong>OTR</strong> is their FP-equivalent payout and <strong>Flat Labor</strong>
                      their subcontractor labor.</li>
                  <li><strong>Interstate:</strong> — a heading, not a figure. The three rows beneath it
                      split what used to be a single "OTR Margin" number:
                    <ul>
                      <li><strong>Insurance Revenue</strong> — valuation charged to the customer.</li>
                      <li><strong>Margin on FP OTRs</strong> — what OTR keeps after the FP payout, labor,
                          valuation, third-party charges and any agency payout.</li>
                      <li><strong>Agency &amp; Pass-Through</strong> — negotiated payouts on Third-Party jobs.</li>
                    </ul>
                    Third-party pass-through charges are not shown: they are billed and paid at cost, so
                    they net to zero.</li>
                  <li><strong>Partner FP — not shared with a franchise</strong> — interstate jobs with no
                      receiving franchise, so their FP revenue reaches no franchise row.</li>
                </ul>
                <div class="p-mb-2">
                  Note: a job shared into multiple franchises contributes to each,
                  so franchise rows can overlap — that's why OTR Jobs is left blank
                  in the total rows (it isn't additive across franchises).
                </div>
                <!-- #6751: names the excluded franchise so anyone reconciling a gap knows what to
                     search for, without putting the row back on the table. -->
                <div class="p-mb-2">
                  Note: <strong>Toledo</strong> is an internal test franchise and is excluded from
                  this table and its totals. Its Local revenue is still shown in full on the
                  System Revenue dashboard, and interstate jobs shared into it still appear in the
                  drill-down below (and in the figures under Interstate).
                </div>
                <div class="p-mb-2">
                  <a
                    [routerLink]="['/reports', 'dynamic', 'new']"
                    [queryParams]="otrSystemRevenueReportParams$ | async"
                    target="_blank"
                    >See OTR jobs</a
                  >.
                </div>
              </app-dashboard-info-v2>
            </div>
          </div>
          <p-splitButton
            label="Copy Data"
            icon="pi pi-copy"
            (onClick)="vm.copyToClipboard()"
            [model]="vm.actions"
            [disabled]="vm.loading"
            appendTo="body"
          ></p-splitButton>
        </div>

        <p-blockUI
          [target]="otrRevenueTable"
          [blocked]="vm.loading"
          [autoZIndex]="false"
        >
          <i class="pi pi-spin pi-spinner" style="font-size: 3rem"></i>
        </p-blockUI>

        <app-blockable #otrRevenueTable>
          <div class="table-scroll" *ngIf="!vm.isEmpty">
            <table class="events-table">
              <thead>
                <tr>
                  <th *ngFor="let header of vm.headers">{{ header }}</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let row of vm.rows" [class]="'row-' + row.kind">
                  <td *ngFor="let cell of row.cells">
                    <ng-container
                      *ngIf="$any(cell).routerLink as routerLink; else plainCell"
                    >
                      <a
                        class="freya-link-button"
                        [routerLink]="routerLink"
                        [queryParams]="$any(cell).queryParams"
                        target="_blank"
                        >{{ $any(cell).text }}</a
                      >
                    </ng-container>
                    <ng-template #plainCell>{{ cell }}</ng-template>
                  </td>
                </tr>
              </tbody>
            </table>
            <!-- #6765 / #6855: data-quality warnings, rendered under the table rather than in the
                 info panel. A figure here can be well above its true value, and a caveat behind an
                 icon most people never open is not a caveat. #6855 turned the single margin
                 footnote into a list and gave each alert the jobs that caused it as links, so
                 accounting clicks straight through instead of copying a code into search. The list
                 is empty when the data is clean, so the block disappears by itself. -->
            <div class="dashboard-alerts" *ngIf="vm.alerts.length">
              <p
                class="dashboard-alert"
                *ngFor="let alert of vm.alerts; trackBy: trackAlert"
                [ngClass]="'sev-' + alert.severity"
              >
                {{ alert.text }}
                <ng-container *ngFor="let job of alert.jobs; let last = last">
                  <a
                    class="freya-link-button"
                    [routerLink]="job.routerLink"
                    [queryParams]="job.queryParams"
                    target="_blank"
                    >{{ job.code }}</a
                  ><span *ngIf="!last">, </span>
                </ng-container>
              </p>
            </div>
          </div>
          <app-dashboard-empty-state
            *ngIf="vm.isEmpty"
            text="No OTR revenue activity in the selected period"
          ></app-dashboard-empty-state>
        </app-blockable>
      </div>
    </div>
  </app-dashboard-shell>
</ng-container>
